import {
  ChangeEvent,
  MouseEvent,
  useState,
  useEffect,
  Dispatch,
  SetStateAction,
  useCallback,
} from "react";
import { PagedResult } from "marvin-auth-kit";
import { Pagination } from "../types";

export function usePagination(
  data?: PagedResult<unknown> | undefined,
  validLimits: number[] = [5, 10, 25, 50, 100]
): [
  Pagination,
  Dispatch<SetStateAction<Pagination>>,
  (event: MouseEvent<HTMLButtonElement> | null, page: number) => void,
  (event: ChangeEvent<HTMLInputElement>) => void
] {
  const closest = useCallback(
    (goal: number, counts: number[] = validLimits) =>
      counts.reduce(function (prev, curr) {
        return Math.abs(curr - goal) < Math.abs(prev - goal) ? curr : prev;
      }),
    [validLimits]
  );
  const initialPagination: Pagination = data
    ? {
        ...data,
        limit: closest(data.limit),
        total: data.totalCount,
      }
    : {
        offset: 0,
        limit: validLimits[validLimits.length - 2],
        total: 0,
      };
  const [pagination, setPagination] = useState<Pagination>(initialPagination);

  useEffect(() => {
    if (data && pagination.total !== data.totalCount) {
      setPagination({ ...pagination, total: data.totalCount });
    }
  }, [data, pagination]);

  function onChangePage(
    event: MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) {
    const newPagination = { ...pagination, offset: newPage };
    setPagination(newPagination);
  }

  function onChangeRowsPerPage(event: ChangeEvent<HTMLInputElement>) {
    const { value } = event.target;
    const newPagination = {
      ...pagination,
      limit: closest(parseInt(value, 10)),
      offset: 0,
    };

    setPagination(newPagination);
  }

  return [pagination, setPagination, onChangePage, onChangeRowsPerPage];
}
